import { ThinkingLevel } from "@earendil-works/pi-agent-core";

export type AgentSpecificationParams = {
  id: string;
  role: string;
  systemPrompt: string;
  excludedTools?: readonly string[];
  toolRestrictions?: Record<string, readonly string[]>;
  model?: string;
  thinkingLevel?: ThinkingLevel;
  disableBuiltinTools?: boolean;
  disableExtensions?: boolean;
  disableSkills?: boolean;
  disablePromptTemplates?: boolean;
  disableContextFiles?: boolean;
  /** Allowlist of skill names to load. Empty = use default discovery. */
  skills?: readonly string[];
  /** Denylist of skill names to disable. Overrides `skills`. */
  excludedSkills?: readonly string[];
  ephemeral?: boolean;
  /** Working directory for the agent process (defaults to process.cwd()). */
  cwd?: string;
};

/**
 * Build the toolRestrictions map from params.toolRestrictions.
 *
 * Returns a shallow copy of params.toolRestrictions, or an empty object
 * if none are provided. Every allowed tool is a key in this map —
 * unrestricted tools have an empty array (`[]`).
 */
export function buildToolRestrictions(
  params: Pick<AgentSpecificationParams, "toolRestrictions">,
): Record<string, readonly string[]> {
  return { ...params.toolRestrictions };
}

/**
 * Immutable specification that defines what an agent is and how it should behave.
 *
 * Maps to pi CLI flags and `createAgentSession` options. Every field has a
 * sensible default — subclasses only set what they need to override.
 */
export abstract class AgentSpecification {
  public readonly id: string;
  public readonly role: string;
  public readonly systemPrompt: string;

  /** Denylist of tool names to disable even if they'd otherwise be active. */
  public readonly excludedTools: readonly string[] = [];
  /**
   * Per-tool pattern restrictions that limit what an agent can do with
   * granted tools.
   *
   * This is the single source of truth for tool configuration. Every
   * allowed tool is a key in this map:
   * - Unrestricted tools have an empty array (`[]`).
   * - Restricted tools have an array of glob patterns (e.g.
   *   `["git *", "npm *"]`).
   *
   * A tool call passes when its input value matches at least one
   * positive pattern and no negation (`!`) pattern. Tools listed in the
   * map but not recognised by the restriction interceptor are always
   * blocked.
   *
   * Unlike {@link excludedTools} (which removes a tool entirely from the
   * active set), `toolRestrictions` keeps the tool available but limits
   * its inputs.
   */
  public readonly toolRestrictions: Record<string, readonly string[]> = {};
  /** Model pattern (e.g. "claude-sonnet-4-5"). Undefined = use default. */
  public readonly model?: string | undefined;
  /** Thinking/reasoning level. Undefined = use default. */
  public readonly thinkingLevel?: ThinkingLevel | undefined;
  /** Disable all built-in tools (read, bash, edit, write, etc.). */
  public readonly disableBuiltinTools: boolean = false;
  /** Disable extension discovery and loading. */
  public readonly disableExtensions: boolean = false;
  /** Disable skill discovery and loading. */
  public readonly disableSkills: boolean = false;
  /** Disable prompt template discovery and loading. */
  public readonly disablePromptTemplates: boolean = false;
  /** Disable AGENTS.md and CLAUDE.md auto-loading. */
  public readonly disableContextFiles: boolean = false;
  /** Don't persist the session to disk (ephemeral agent). */
  public readonly ephemeral: boolean = false;
  /** Allowlist of skill names to load. Empty = use default discovery. */
  public readonly skills: readonly string[] = [];
  /** Denylist of skill names to disable. Overrides `skills`. */
  public readonly excludedSkills: readonly string[] = [];
  /** Working directory for the agent process. */
  public readonly cwd?: string | undefined;

  /**
   * Allowlist of tool names. Empty = use default tools.
   *
   * Derived from {@link toolRestrictions}: every key in the map
   * represents an allowed tool.
   */
  public get tools(): readonly string[] {
    return Object.keys(this.toolRestrictions);
  }

  /**
   * Parse `excludedTools` entries into full exclusions and partial
   * restrictions.
   *
   * - Entry without `:` (e.g. `"bash"`) → full exclusion: the tool is
   *   removed from the active tool set entirely.
   * - Entry with `:` (e.g. `"bash:rm *"`) → partial restriction: the
   *   tool stays active but the pattern limits its inputs.
   */
  static parseExcludedTools(excludedTools: readonly string[]): {
    fullExclusions: Set<string>;
    partialRestrictions: Record<string, string[]>;
  } {
    const fullExclusions = new Set<string>();
    const partialRestrictions: Record<string, string[]> = {};
    for (const entry of excludedTools) {
      const colonIndex = entry.indexOf(":");
      if (colonIndex === -1) {
        fullExclusions.add(entry);
      } else {
        const tool = entry.slice(0, colonIndex);
        const pattern = entry.slice(colonIndex + 1);
        if (!partialRestrictions[tool]) partialRestrictions[tool] = [];
        partialRestrictions[tool].push(pattern);
      }
    }
    return { fullExclusions, partialRestrictions };
  }

  constructor(params: AgentSpecificationParams) {
    if (!params.id || params.id.trim().length === 0) {
      throw new Error("AgentSpecification id must not be empty");
    }
    this.id = params.id;
    this.role = params.role;
    this.systemPrompt = params.systemPrompt;
    this.excludedTools = params.excludedTools ?? [];
    this.toolRestrictions = buildToolRestrictions(params);
    this.model = params.model;
    this.thinkingLevel = params.thinkingLevel;
    this.disableBuiltinTools = params.disableBuiltinTools ?? false;
    this.disableExtensions = params.disableExtensions ?? false;
    this.disableSkills = params.disableSkills ?? false;
    this.disablePromptTemplates = params.disablePromptTemplates ?? false;
    this.disableContextFiles = params.disableContextFiles ?? false;
    this.ephemeral = params.ephemeral ?? false;
    this.skills = params.skills ?? [];
    this.excludedSkills = params.excludedSkills ?? [];
    this.cwd = params.cwd;
  }

  /**
   * Serialize this specification to a plain object matching
   * {@link AgentSpecificationParams}. Enables cloning and modifying a spec
   * (e.g. adding thinkingLevel from a model preset).
   */
  toJSON(): AgentSpecificationParams {
    return {
      id: this.id,
      role: this.role,
      systemPrompt: this.systemPrompt,
      excludedTools: this.excludedTools,
      toolRestrictions: this.toolRestrictions,
      skills: this.skills,
      excludedSkills: this.excludedSkills,
      model: this.model,
      thinkingLevel: this.thinkingLevel,
      disableBuiltinTools: this.disableBuiltinTools,
      disableExtensions: this.disableExtensions,
      disableSkills: this.disableSkills,
      disablePromptTemplates: this.disablePromptTemplates,
      disableContextFiles: this.disableContextFiles,
      ephemeral: this.ephemeral,
      cwd: this.cwd,
    };
  }
}
